import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import {
  Loader2, RefreshCw, Trees, Home as HomeIcon, ClipboardList, CalendarRange,
  ShieldCheck, ChevronRight,
} from "lucide-react";

/* Operator Overview — portfolio-level snapshot.
 *
 * Strictly operational: parks, plots, open jobs, jobs this month, compliance.
 * Per user directive: NO maps/GIS, NO advanced asset hierarchies.
 */
export default function OperatorOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/operator-dashboard");
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="text-[#475569] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading operator overview…
      </div>
    );
  }
  const k = data.kpis;

  return (
    <div data-testid="operator-overview-page">
      <div className="admin-page-header flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Operations · Portfolio</p>
          <h1>Operator Overview</h1>
          <p className="lead">A single-pane snapshot of every park, plot and live job under PHS care.</p>
        </div>
        <button onClick={load} className="btn-outline h-10 py-0 px-3" data-testid="operator-overview-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="operator-overview-kpis">
        <Kpi label="Parks" value={k.parks_total} sub={`${k.parks_active} active`} icon={<Trees size={14} />} tone="green" />
        <Kpi label="Plots" value={k.plots_total} sub={`${k.plots_occupied} occupied`} icon={<HomeIcon size={14} />} tone="teal" />
        <Kpi label="Open jobs" value={k.open_jobs} sub="across portfolio" icon={<ClipboardList size={14} />} tone={k.open_jobs > 50 ? "amber" : "muted"} />
        <Kpi label="Jobs this month" value={k.jobs_this_month} sub="created since 1st" icon={<CalendarRange size={14} />} tone="muted" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <section className="admin-panel overflow-hidden" data-testid="operator-overview-compliance">
          <header className="px-5 py-4 border-b border-[#E2DCD0] bg-gradient-to-b from-[#F7F4ED]/80 to-white flex items-center gap-3">
            <span className="w-2 h-8 rounded-r bg-[#064E2E]" />
            <div>
              <h3 className="font-display text-lg text-[#071A33] tracking-tight">Compliance status</h3>
              <p className="text-[11px] text-[#475569]">Share of plots flagged as active.</p>
            </div>
          </header>
          <div className="p-6 flex items-center gap-5">
            <div className="relative w-32 h-32 shrink-0">
              <CompliancePie pct={k.compliance_pct} />
            </div>
            <div>
              <p className="font-display text-5xl text-[#071A33] tabular-nums leading-none">
                {k.compliance_pct}%
              </p>
              <p className="text-[12px] text-[#475569] mt-2 max-w-xs">
                Of {k.plots_total} plot{k.plots_total === 1 ? "" : "s"} on record, {k.plots_active} are currently active.
              </p>
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#0F766E] font-semibold mt-3 inline-flex items-center gap-1">
                <ShieldCheck size={11} /> Lightweight check, no audit history
              </p>
            </div>
          </div>
        </section>

        <section className="admin-panel overflow-hidden" data-testid="operator-overview-busiest">
          <header className="px-5 py-4 border-b border-[#E2DCD0] bg-gradient-to-b from-[#F7F4ED]/80 to-white flex items-center gap-3">
            <span className="w-2 h-8 rounded-r bg-[#B88A2A]" />
            <div>
              <h3 className="font-display text-lg text-[#071A33] tracking-tight">Busiest parks right now</h3>
              <p className="text-[11px] text-[#475569]">Top open-job counts. Click to drill in.</p>
            </div>
          </header>
          {data.busiest_parks.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-[#475569] italic">No parks with open jobs.</p>
          ) : (
            <ul className="divide-y divide-[#E2DCD0]">
              {data.busiest_parks.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/adminpanel/assets/parks/${p.id}`}
                    className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-[#F7F4ED]/70"
                    data-testid={`operator-overview-park-${p.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#071A33] truncate">{p.name}</p>
                      <p className="text-[11px] text-[#475569] truncate">{p.operator_name}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[#B88A2A] font-semibold tabular-nums">{p.open_jobs}</span>
                      <ChevronRight size={14} className="text-[#94A3B8]" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon, tone }) {
  const cls = tone === "green" ? "border-[#064E2E]/30 bg-[#064E2E]/5"
            : tone === "teal"  ? "border-[#0F766E]/30 bg-[#0F766E]/5"
            : tone === "amber" ? "border-[#B88A2A]/30 bg-[#B88A2A]/5"
            : "border-[#E2DCD0] bg-white";
  return (
    <div className={`border rounded-xl p-4 ${cls}`}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] flex items-center gap-1.5">
        {icon}{label}
      </p>
      <p className="font-display text-3xl text-[#071A33] tabular-nums mt-1 tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-[#475569] mt-0.5">{sub}</p>}
    </div>
  );
}

function CompliancePie({ pct }) {
  // SVG ring chart — pure CSS, no external dep.
  const r = 56;
  const c = 2 * Math.PI * r;
  const safe = Math.max(0, Math.min(100, Number(pct) || 0));
  const offset = c - (safe / 100) * c;
  const colour = safe >= 90 ? "#064E2E" : safe >= 70 ? "#B88A2A" : "#B91C1C";
  return (
    <svg viewBox="0 0 128 128" className="w-full h-full">
      <circle cx="64" cy="64" r={r} stroke="#E2DCD0" strokeWidth="12" fill="none" />
      <circle
        cx="64" cy="64" r={r}
        stroke={colour} strokeWidth="12" fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 64 64)"
      />
    </svg>
  );
}
